import React, { useCallback, useMemo, useState } from 'react';
import Icon from 'carbon-react/lib/components/icon';

import { DispatchedReportItem } from '@futrli-sdk/report_scheduling';
import { FormattedMessage } from '@futrli/localisation';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';
import { ScheduleDerivedStatus } from '@futrli/views/processors/reportScheduling/reportSchedulingList';

import { openPdfForViewing, downloadPdfFile } from '../../../../utils/pdf-actions';

interface UseScheduleActionItemsArgs {
  status: ScheduleDerivedStatus;
  awaitingReviewItem?: DispatchedReportItem;
  reportId?: string | null;
  reportName?: string;
  scheduleId?: string;
  onDelete: () => void;
}

export const useScheduleActionItems = ({ status, awaitingReviewItem, reportId, reportName, scheduleId, onDelete }: UseScheduleActionItemsArgs) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { scheduleReport } = useSidebarContext();

  const handleViewPdf = useCallback(async () => {
    if (!awaitingReviewItem?.download_link || !awaitingReviewItem?.name) {
      return;
    }
    await openPdfForViewing(awaitingReviewItem.download_link);
  }, [awaitingReviewItem]);

  const handleDownloadPdf = useCallback(async () => {
    if (!awaitingReviewItem?.download_link || !awaitingReviewItem?.name) {
      return;
    }
    try {
      setIsDownloading(true);
      await downloadPdfFile(awaitingReviewItem.download_link, `${awaitingReviewItem.name}.pdf`);
    } finally {
      setIsDownloading(false);
    }
  }, [awaitingReviewItem]);

  const items = useMemo(() => [
    ...(scheduleId && reportId
      ? [
          {
            label: <FormattedMessage id="predict.actions.manage_schedule" defaultMessage="Manage schedule" />,
            onClick: () => {
              if (reportId && reportName) {
                scheduleReport.open({ reportId, reportName, scheduleId });
              }
            }
          }
        ]
      : []),
    ...(status === 'awaiting-review'
      ? [
          {
            label: <FormattedMessage id="predict.actions.view_pdf" defaultMessage="View PDF" />,
            onClick: handleViewPdf
          },
          {
            label: <FormattedMessage id="predict.actions.download_pdf" defaultMessage="Download PDF" />,
            onClick: handleDownloadPdf,
            disabled: isDownloading
          }
        ]
      : []),
    {
      label: (
        <>
          <Icon type="delete" fontSize="small" />
          <FormattedMessage id="common.delete" defaultMessage="Delete" />
        </>
      ),
      onClick: onDelete,
      variant: 'danger' as const
    }
  ], [status, handleViewPdf, handleDownloadPdf, isDownloading, onDelete, reportId, reportName, scheduleId, scheduleReport]);

  return items;
};
